import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ArticlesGrid from '../components/ArticlesGrid';
import Pagination from '../components/Pagination';
import { useSiteData } from '../data/SiteDataContext';
import { normalizeTagValue } from '../../utils/tagUtils';
import { classifyGroups, collectGroupTags, countTagUsage } from '../lib/tagIndex';
import { POSTS_PER_PAGE } from './HomePage';

// Ported from the original app's sidebar: the INDEX card's
// Subjects/Geography/Persons tabs plus the multi-select TAGS card.
const INDEX_TABS = [
    { id: 'subjects', label: 'Subjects' },
    { id: 'geography', label: 'Geography' },
    { id: 'persons', label: 'Persons' },
];

function TagsPage() {
    const { status, posts, groups } = useSiteData();
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState('subjects');

    const selection = useMemo(() => {
        const raw = searchParams.get('sel') || '';
        return [...new Set(raw.split(',').map((tag) => normalizeTagValue(tag)).filter(Boolean))];
    }, [searchParams]);
    const selectedSet = useMemo(() => new Set(selection), [selection]);

    const { subjects, geography, persons } = useMemo(() => classifyGroups(groups), [groups]);
    const geographyTags = useMemo(() => collectGroupTags(geography), [geography]);
    const personTags = useMemo(() => collectGroupTags(persons), [persons]);

    const subjectGroups = useMemo(() => (
        subjects
            .map((group) => ({
                id: group.id || group.key,
                label: group.label,
                tags: (Array.isArray(group.subTags) ? group.subTags : [])
                    .map((tag) => normalizeTagValue(tag))
                    .filter(Boolean),
            }))
            .filter((group) => group.id && group.tags.length > 0)
    ), [subjects]);

    // Tags shown in the cloud: every tag in use on a post, minus the ones that
    // belong to the geography/persons indexes (original "subjectTags" logic).
    const hiddenSet = useMemo(() => new Set([
        ...geographyTags.map((tag) => tag.value),
        ...personTags.map((tag) => tag.value),
    ]), [geographyTags, personTags]);

    const cloudTags = useMemo(() => (
        [...countTagUsage(posts).keys()]
            .filter((tag) => !hiddenSet.has(tag))
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    ), [posts, hiddenSet]);

    const applySelection = (nextTags) => {
        const cleaned = [...new Set(nextTags)].filter(Boolean);
        // Selection changes reset pagination (no `p`).
        setSearchParams(cleaned.length > 0 ? { sel: cleaned.join(',') } : {});
    };

    const toggleTag = (tag) => {
        applySelection(
            selectedSet.has(tag)
                ? selection.filter((existing) => existing !== tag)
                : [...selection, tag]
        );
    };

    const toggleGroup = (group) => {
        const allSelected = group.tags.every((tag) => selectedSet.has(tag));
        const next = new Set(selection);
        group.tags.forEach((tag) => (allSelected ? next.delete(tag) : next.add(tag)));
        applySelection([...next]);
    };

    // Same OR semantics as the original backend filter (tags: { $in: [...] }).
    const filteredPosts = useMemo(() => {
        if (selection.length === 0) {
            return posts;
        }
        return posts.filter((post) => (
            Array.isArray(post.tags)
            && post.tags.some((tag) => selectedSet.has(normalizeTagValue(tag)))
        ));
    }, [posts, selection, selectedSet]);

    if (status === 'loading') {
        return <p className="small-meta site-loading">Loading …</p>;
    }

    if (status === 'error') {
        return <p className="small-meta site-loading">Failed to load documents. Refresh to retry.</p>;
    }

    const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
    const requestedPage = parseInt(searchParams.get('p'), 10);
    const currentPage = Math.min(
        Math.max(1, Number.isFinite(requestedPage) ? requestedPage : 1),
        totalPages
    );
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    const pagePosts = filteredPosts.slice(start, start + POSTS_PER_PAGE);

    const getPageLink = (page) => {
        const params = new URLSearchParams();
        if (selection.length > 0) {
            params.set('sel', selection.join(','));
        }
        if (page > 1) {
            params.set('p', String(page));
        }
        const query = params.toString();
        return query ? `/tags?${query}` : '/tags';
    };

    const visibleTabs = INDEX_TABS.filter((tab) => {
        if (tab.id === 'geography') return geographyTags.length > 0;
        if (tab.id === 'persons') return personTags.length > 0;
        return true;
    });

    const renderChip = (value, label = value) => (
        <button
            key={value}
            type="button"
            className={`ds-tag-chip${selectedSet.has(value) ? ' active' : ''}`}
            aria-pressed={selectedSet.has(value)}
            onClick={() => toggleTag(value)}
        >
            {label}
        </button>
    );

    return (
        <>
            <header className="archive-header">
                <div className="eyebrow" style={{ marginBottom: '.4rem' }}>Index</div>
                <h1>Browse by Tag</h1>
            </header>

            {visibleTabs.length > 1 && (
                <div className="index-tabs-row" role="tablist" aria-label="Index sections">
                    {visibleTabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={tab.id === activeTab}
                            className={`index-tab-btn${tab.id === activeTab ? ' active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            <div className="index-panel">
                {activeTab === 'subjects' && (
                    <div className="tag-cloud" role="tabpanel" aria-label="Subject groups">
                        {subjectGroups.map((group) => {
                            const allSelected = group.tags.every((tag) => selectedSet.has(tag));
                            return (
                                <button
                                    key={group.id}
                                    type="button"
                                    className={`ds-tag-chip${allSelected ? ' active' : ''}`}
                                    aria-pressed={allSelected}
                                    onClick={() => toggleGroup(group)}
                                >
                                    {group.label}
                                </button>
                            );
                        })}
                    </div>
                )}
                {activeTab === 'geography' && (
                    <div className="tag-cloud" role="tabpanel" aria-label="Geography tags">
                        {geographyTags.map((tag) => renderChip(tag.value, tag.label))}
                    </div>
                )}
                {activeTab === 'persons' && (
                    <div className="tag-cloud" role="tabpanel" aria-label="Person tags">
                        {personTags.map((tag) => renderChip(tag.value, tag.label))}
                    </div>
                )}
            </div>

            {activeTab === 'subjects' && cloudTags.length > 0 && (
                <div className="index-panel">
                    <div className="article-tags-label">Tags</div>
                    <div className="tag-cloud" aria-label="All tags">
                        {cloudTags.map((tag) => renderChip(tag))}
                    </div>
                </div>
            )}

            <div className="index-results-meta">
                <span>
                    {filteredPosts.length} document{filteredPosts.length === 1 ? '' : 's'}
                    {selection.length > 0 && ` · ${selection.length} tag${selection.length === 1 ? '' : 's'} selected`}
                </span>
                {selection.length > 0 && (
                    <button
                        type="button"
                        className="index-clear-link"
                        onClick={() => applySelection([])}
                    >
                        Remove all tags ✕
                    </button>
                )}
            </div>

            {filteredPosts.length === 0 ? (
                <p className="small-meta site-loading">No documents match the selected tags.</p>
            ) : (
                <>
                    <ArticlesGrid posts={pagePosts} />
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        getPageLink={getPageLink}
                        ariaLabel="Tag index pagination"
                    />
                </>
            )}
        </>
    );
}

export default TagsPage;
